import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '../App';
import { ArrowLeft, Phone, PhoneOff, Mic, MicOff, Heart } from 'lucide-react';

export default function VoiceCallInterface() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [callActive, setCallActive] = useState(false);
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [timer, setTimer] = useState(null);

  useEffect(() => {
    updateSessionStatus('active');
    return () => {
      if (timer) clearInterval(timer);
    };
  }, []);

  const updateSessionStatus = async (status) => {
    try {
      await axios.put(`${API}/sessions/${sessionId}/status?status=${status}`);
    } catch (error) {
      console.error('Failed to update session status');
    }
  };

  const startCall = () => {
    setCallActive(true);
    toast.success('Call connected');
    
    // Start timer
    const interval = setInterval(() => {
      setDuration(prev => prev + 1);
    }, 1000);
    setTimer(interval);
  };

  const endCall = async () => {
    setCallActive(false);
    if (timer) clearInterval(timer);
    
    try {
      await updateSessionStatus('completed');
      toast.success('Call ended');
      if (user?.role === 'care_seeker') {
        navigate('/seeker-dashboard');
      } else {
        navigate('/provider-dashboard');
      }
    } catch (error) {
      toast.error('Failed to end call');
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
    toast.info(muted ? 'Microphone unmuted' : 'Microphone muted');
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#5B8C79] to-[#4A7263] flex flex-col">
      {/* Header */}
      <header className="glass-effect border-b border-white/20">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-btn"
            onClick={() => navigate(user?.role === 'care_seeker' ? '/seeker-dashboard' : '/provider-dashboard')}
            className="text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-2 text-white">
            <Heart size={24} />
            <span className="font-heading font-semibold">Anonymous Voice Call</span>
          </div>
          <div className="w-6" />
        </div>
      </header>

      {/* Call Interface */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="text-center mb-12">
          <div className="w-32 h-32 rounded-full bg-white/20 backdrop-blur-lg flex items-center justify-center mb-6 mx-auto">
            <Phone size={48} className="text-white" />
          </div>
          <h2 className="text-2xl font-heading font-semibold text-white mb-2">
            {callActive ? 'Call in Progress' : 'Ready to Connect'}
          </h2>
          {callActive && (
            <div className="text-4xl font-mono text-white/90 mt-4">
              {formatDuration(duration)}
            </div>
          )}
          <p className="text-white/70 mt-2">
            {callActive ? 'Secure anonymous voice connection' : 'Click to start the call'}
          </p>
        </div>

        <div className="flex gap-6">
          {!callActive ? (
            <button
              data-testid="start-call-btn"
              onClick={startCall}
              className="w-20 h-20 rounded-full bg-white hover:bg-white/90 flex items-center justify-center transition-all hover:scale-110 shadow-xl"
            >
              <Phone size={32} className="text-primary" />
            </button>
          ) : (
            <>
              <button
                data-testid="toggle-mute-btn"
                onClick={toggleMute}
                className={`w-16 h-16 rounded-full ${
                  muted ? 'bg-destructive' : 'bg-white/20'
                } backdrop-blur-lg flex items-center justify-center transition-all hover:scale-110`}
              >
                {muted ? <MicOff size={24} className="text-white" /> : <Mic size={24} className="text-white" />}
              </button>
              <button
                data-testid="end-call-btn"
                onClick={endCall}
                className="w-20 h-20 rounded-full bg-destructive hover:bg-destructive/90 flex items-center justify-center transition-all hover:scale-110 shadow-xl"
              >
                <PhoneOff size={32} className="text-white" />
              </button>
            </>
          )}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-lg text-white text-sm">
            <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span>Encrypted Connection</span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="py-8 text-center text-white/60 text-sm">
        <p>Your identity remains anonymous throughout the call</p>
      </div>
    </div>
  );
}